import { Col, Row } from "react-grid-system";
import {
  DateString,
  ValidationErrors,
  VerifiedHistory,
} from "../../../lib/api/models";
import { useGetVerifiedHistory } from "../../../lib/api/queries";
import Spinner from "../../common/Spinner";
import StyledError from "../../common/StyledError";

import StyledButton from "../../common/buttons/StyledButton";
import DateInput from "../../common/inputs/DateInput";
import StyledCheckbox from "../../common/inputs/StyledCheckbox";
import Table, { TableColumn } from "../../common/tables/Table/Table";
import styles from "./CMInfoVerifiedHistory.module.css";
import {
  convertDateStringToMonthDayYearFormat,
  formatToDateStringInCentralTime,
} from "../../../lib/formatting";
import { useState } from "react";
import { subMonths } from "date-fns/subMonths";
import { format } from "date-fns/format";
import { MIN_START_AND_END_DATE_FOR_HISTORY } from "../constants";

const strings = {
  verifiedHistoryErrorTitle: "Failed to load Verified History",
  search: "Search",
  startDate: "Start Date",
  endDate: "End Date",
  verifiedDate: "VERIFIED DATE",
  agent: "AGENT",
  source: "SOURCE",
  ssn: "SSN",
  dob: "DOB",
  mmn: "MMN",
  securityWord: "SECURITY WORD",
  billingAddress: "BILLING ADDRESS",
  primaryEmail: "PRIMARY EMAIL",
  alternateEmail: "ALTERNATE EMAIL",
  emailOptOut: "EMAIL OPT OUT",
  placeOfEmploymentAndSourceOfIncome: "POE/SOI",
  school: "SCHOOL",
  p2d: "P2D",
  futureStartDateErrorMessage: "Start Date cannot be in the future",
  pastStartDateErrorMessage: (date: string) =>
    `Start Date cannot be before ${date}`,
  futureEndDateErrorMessage: "End Date cannot be in the future",
  pastEndDateErrorMessage: (date: string) =>
    `End Date cannot be before ${date}`,
  startDatePastEndDate: "Start Date cannot be after End Date",
  noVerifiedHistory:
    "No verified history results match current search criteria",

  ssnVerified: "SSN Verified",
  dobVerified: "Date of Birth Verified",
  motherMaidenVerified: "Mother's Maiden Name Verified",
  securityVerified: "Security Word Verified",
  addressVerified: "Billing Address Verified",
  primaryEmailVerified: "Primary Email Verified",
  alternateEmailVerified: "Alternate Email Verified",
  emailOptOutVerified: "Email Opt Out Verified",
  incomeSourceVerified: "Income Soruce Verified",
  schoolVerified: "School Verified",
  p2dVerified: "Permission to Discuss Verified",
};

type Props = {
  dwbuid: string;
  referenceId?: string;
};

export default function CMInfoVerifiedHistory({ dwbuid, referenceId }: Props) {
  const today = format(new Date(), "yyyy-MM-dd").toString() as DateString;

  const [searchStartDate, setSearchStartDate] = useState(
    formatToDateStringInCentralTime(subMonths(new Date(), 6)),
  );
  const [searchEndDate, setSearchEndDate] = useState(
    formatToDateStringInCentralTime(new Date()),
  );

  const [startDate, setStartDate] = useState(searchStartDate);
  const [endDate, setEndDate] = useState(searchEndDate);

  const [validationMessages, setValidationMessages] = useState<
    ValidationErrors | undefined
  >();

  const {
    data: getVerifiedHistoryResponse,
    isLoading: isGetVerifiedHistoryLoading,
    isError: isGetVerifiedHistoryError,
    error: getVerifiedHistoryError,
    refetch: getVerifiedHistoryRefetch,
  } = useGetVerifiedHistory({
    dwbuid,
    referenceId,
    startDate: searchStartDate,
    endDate: searchEndDate,
  });

  const columns: TableColumn<VerifiedHistory>[] = [
    {
      header: strings.verifiedDate,
      size: 2,
      alignment: "left",
      render: (row) => convertDateStringToMonthDayYearFormat(row.verifiedDate),
    },
    {
      header: strings.agent,
      size: 2,
      alignment: "left",
      render: (row) => row.agent,
    },
    {
      header: strings.ssn,
      size: 2,
      alignment: "left",
      render: (row) =>
        row.ssnLast4Verified ? (
          <StyledCheckbox
            aria-label={strings.ssnVerified}
            className={styles.checkbox}
            checked={row.ssnLast4Verified}
            readOnly
          />
        ) : null,
    },
    {
      header: strings.dob,
      size: 2,
      alignment: "left",
      render: (row) =>
        row.dateOfBirthVerified ? (
          <StyledCheckbox
            aria-label={strings.dobVerified}
            className={styles.checkbox}
            checked={row.dateOfBirthVerified}
            readOnly
          />
        ) : null,
    },
    {
      header: strings.mmn,
      size: 2,
      alignment: "left",
      render: (row) =>
        row.mothersMaidenNameVerified ? (
          <StyledCheckbox
            aria-label={strings.motherMaidenVerified}
            className={styles.checkbox}
            checked={row.mothersMaidenNameVerified}
            readOnly
          />
        ) : null,
    },
    {
      header: strings.securityWord,
      size: 2,
      alignment: "left",
      render: (row) =>
        row.securityVerified ? (
          <StyledCheckbox
            aria-label={strings.securityVerified}
            className={styles.checkbox}
            checked={row.securityVerified}
            readOnly
          />
        ) : null,
    },
    {
      header: strings.billingAddress,
      size: 2,
      alignment: "left",
      render: (row) =>
        row.addressVerified ? (
          <StyledCheckbox
            aria-label={strings.addressVerified}
            className={styles.checkbox}
            checked={row.addressVerified}
            readOnly
          />
        ) : null,
    },
    {
      header: strings.primaryEmail,
      size: 2,
      alignment: "left",
      render: (row) =>
        row.primaryEmailVerified ? (
          <StyledCheckbox
            aria-label={strings.primaryEmailVerified}
            className={styles.checkbox}
            checked={row.primaryEmailVerified}
            readOnly
          />
        ) : null,
    },
    {
      header: strings.alternateEmail,
      size: 2,
      alignment: "left",
      render: (row) =>
        row.secondaryEmailVerified ? (
          <StyledCheckbox
            aria-label={strings.alternateEmailVerified}
            className={styles.checkbox}
            checked={row.secondaryEmailVerified}
            readOnly
          />
        ) : null,
    },
    {
      header: strings.emailOptOut,
      size: 2,
      alignment: "left",
      render: (row) =>
        row.emailOptoutVerified ? (
          <StyledCheckbox
            aria-label={strings.emailOptOutVerified}
            className={styles.checkbox}
            checked={row.emailOptoutVerified}
            readOnly
          />
        ) : null,
    },
    {
      header: strings.placeOfEmploymentAndSourceOfIncome,
      size: 2,
      alignment: "left",
      render: (row) =>
        row.incomeSourceVerified ? (
          <StyledCheckbox
            aria-label={strings.incomeSourceVerified}
            className={styles.checkbox}
            checked={row.incomeSourceVerified}
            readOnly
          />
        ) : null,
    },
    {
      header: strings.school,
      size: 2,
      alignment: "left",
      render: (row) =>
        row.schoolVerified ? (
          <StyledCheckbox
            aria-label={strings.schoolVerified}
            className={styles.checkbox}
            checked={row.schoolVerified}
            readOnly
          />
        ) : null,
    },
    {
      header: strings.p2d,
      size: 2,
      alignment: "left",
      render: (row) =>
        row.permissionToDiscussVerified ? (
          <StyledCheckbox
            aria-label={strings.p2dVerified}
            className={styles.checkbox}
            checked={row.permissionToDiscussVerified}
            readOnly
          />
        ) : null,
    },
  ];

  return isGetVerifiedHistoryLoading ? (
    <Row>
      <Col>
        <Spinner />
      </Col>
    </Row>
  ) : isGetVerifiedHistoryError ? (
    <Row>
      <Col>
        <StyledError
          errorTitle={strings.verifiedHistoryErrorTitle}
          error={getVerifiedHistoryError}
          refetch={getVerifiedHistoryRefetch}
        />
      </Col>
    </Row>
  ) : (
    <>
      <Row>
        <Col className={styles.buttonRow}>
          <DateInput
            className={styles.dateInputContainer}
            label={strings.startDate}
            value={startDate}
            onChangeDate={(val) => {
              setStartDate(val);
              setValidationMessages((prev) => ({ ...prev, startDate: [] }));
            }}
            max={today}
            min={MIN_START_AND_END_DATE_FOR_HISTORY}
            showPopover={false}
            validationMessages={validationMessages?.startDate}
          />
          <DateInput
            className={styles.dateInputContainer}
            label={strings.endDate}
            value={endDate}
            onChangeDate={(val) => {
              setEndDate(val);
              setValidationMessages((prev) => ({ ...prev, endDate: [] }));
            }}
            max={today}
            min={MIN_START_AND_END_DATE_FOR_HISTORY}
            showPopover={false}
            validationMessages={validationMessages?.endDate}
          />
          <StyledButton
            className={styles.searchButton}
            onClick={() => {
              const tempEndDateValidation: string[] = [];
              const tempStartDateValidation: string[] = [];
              if (endDate > today) {
                tempEndDateValidation.push(strings.futureEndDateErrorMessage);
              }
              if (endDate < MIN_START_AND_END_DATE_FOR_HISTORY) {
                tempEndDateValidation.push(
                  strings.pastEndDateErrorMessage(
                    convertDateStringToMonthDayYearFormat(
                      MIN_START_AND_END_DATE_FOR_HISTORY,
                    ),
                  ),
                );
              }
              if (startDate > today) {
                tempStartDateValidation.push(
                  strings.futureStartDateErrorMessage,
                );
              }
              if (startDate < MIN_START_AND_END_DATE_FOR_HISTORY) {
                tempStartDateValidation.push(
                  strings.pastStartDateErrorMessage(
                    convertDateStringToMonthDayYearFormat(
                      MIN_START_AND_END_DATE_FOR_HISTORY,
                    ),
                  ),
                );
              }
              if (startDate > endDate) {
                tempStartDateValidation.push(strings.startDatePastEndDate);
              }

              if (
                tempEndDateValidation.length ||
                tempStartDateValidation.length
              ) {
                setValidationMessages({
                  endDate: tempEndDateValidation,
                  startDate: tempStartDateValidation,
                });
              } else {
                setValidationMessages(undefined);
                setSearchStartDate(startDate);
                setSearchEndDate(endDate);
              }
            }}
          >
            {strings.search}
          </StyledButton>
        </Col>
      </Row>
      <Row>
        <Col>
          <Table
            className={styles.table}
            columns={columns}
            data={getVerifiedHistoryResponse.results}
            emptyTableMessage={strings.noVerifiedHistory}
          />
        </Col>
      </Row>
    </>
  );
}
